import classNames from "classnames";
import { TextInput } from "components/Inputs";
import { postDocument } from "actions";
import Datasource, { DatasourceActionType, DatasourceResponse } from "lib/datasource";
import moment from "moment";
import React, { memo, Suspense, useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import ReasonCodeAutocomplete, { MIN_REASON_LENGTH } from "./ReasonCodeAutocomplete";

/** Marker tag sent with review-panel uploads so the server classifies them as RDD documents. */
const RDD_REVIEW_UPLOAD_TAG = "rdd_review_upload";

/** Action type emitted by redux-memoize for memoized action creators. */
const MEMOIZE_ACTION_TYPE = "@redux-memoize/action";

/**
 * Failure action types dispatched by the postDocument / upload / postDocumentRevision
 * lifecycle. The postDocument callback is NOT called on failure — only these action
 * types are dispatched — so we must intercept the dispatch to detect failures.
 */
const UPLOAD_FAILURE_TYPES = new Set([
  "POST_DOCUMENT_FAILED",
  "UPLOAD_DOCUMENT_FAILED",
  "POST_DOCUMENT_REVISION_FAILED",
]);

/**
 * Upload a single file to the given submission condition and return a Promise that:
 *  - resolves (with the file name) when FINALISE_DOCUMENT is dispatched for that document
 *  - rejects (with an Error) when any failure action is dispatched
 *
 * This bypasses the postDocument callback contract (which fires twice on success
 * and never on failure) by intercepting the Redux dispatch chain directly.
 * `postDocument.unmemoized` is used so we can supply our own dispatch without
 * going through the memoize middleware.
 *
 * @param realDispatch - the dispatch function from useDispatch()
 * @param resource - the condition resource { id, link }
 * @param file - the File to upload
 * @param tags - optional tags to attach to the document (e.g. ["rdd_review_upload"])
 */
export function uploadFileTracked(
  realDispatch: (action: any) => any,
  resource: { id: string; link: string },
  file: File,
  tags?: string[]
): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    let settled = false;

    /**
     * An intercepting dispatch that:
     * 1. Forwards plain action objects to realDispatch (so reducers/middleware run normally)
     * 2. Propagates itself through memoized action objects so nested thunks also
     *    route through this interceptor (enabling us to catch FINALISE_DOCUMENT etc.)
     * 3. Calls thunk functions directly (for non-memoized thunks)
     */
    function interceptingDispatch(action: any): any {
      if (typeof action === "function") {
        // Plain thunk — invoke directly with our intercepting dispatch
        return action(interceptingDispatch);
      }

      if (
        action &&
        typeof action === "object" &&
        action.type === MEMOIZE_ACTION_TYPE &&
        action.payload
      ) {
        // redux-memoize action — call the inner thunk via our intercepting dispatch
        // (bypasses memoization, which is acceptable for one-off uploads)
        const { fn, args } = action.payload;
        return fn(...args)(interceptingDispatch);
      }

      // Plain action object — forward to real dispatch so reducers receive it
      const result = realDispatch(action);

      if (!settled && action && typeof action === "object") {
        if (action.type === "FINALISE_DOCUMENT") {
          settled = true;
          resolve(file.name);
        } else if (UPLOAD_FAILURE_TYPES.has(action.type)) {
          settled = true;
          reject(new Error(action.type));
        }
      }

      return result;
    }

    // Invoke the unmemoized thunk directly so our intercepting dispatch propagates
    // through the entire postDocument → upload → postDocumentRevision chain.
    // No callback is passed because we drive state from FINALISE_DOCUMENT.
    // Tags are forwarded as the 5th argument (position preserved, callback=undefined at 4th).
    try {
      (postDocument as any).unmemoized(resource, file, "", undefined, tags)(interceptingDispatch);
    } catch (err) {
      if (!settled) {
        settled = true;
        reject(err instanceof Error ? err : new Error(String(err)));
      }
    }
  });
}

interface Round {
  id: string;
  round_number: number;
  state: string;
}

interface Props {
  taskId: string;
  currentRound: Round | null;
  canLogOutcome: boolean;
  onSuccess: (updatedTask: any) => void;
  /** Team-member list for the @ dropdown. Index 0 must be the "All" sentinel. */
  taggableUsers?: { id: string; name: string }[];
  /** The submission condition resource `{ id, link }` used as the upload target. */
  submissionCondition?: { id: string; link: string } | null;
  /** Called after a successful RDD document upload so the parent can refetch rounds. */
  onUploaded?: () => void;
}

type OutcomeType = "approved" | "resubmit_required";

interface ConfirmationState {
  outcome: OutcomeType;
  reviewedAt: string;
  nextRoundNumber?: number;
}

/**
 * Return true when the serialized HTML from DraftText should be treated as
 * "no notes entered". DraftText serializes an empty editor as "<p></p>" or "".
 */
function isEmptyNotes(html: string): boolean {
  return !html || html === "<p></p>";
}

const ReviewActionPanel = ({
  taskId,
  currentRound,
  canLogOutcome,
  onSuccess,
  taggableUsers = [],
  submissionCondition = null,
  onUploaded,
}: Props) => {
  const dispatch = useDispatch();
  const uploadRef = useRef<HTMLInputElement>(null);
  const [outcome, setOutcome] = useState<OutcomeType>("approved");
  // `notes` now stores serialized HTML from DraftText (e.g. "<p>...</p>").
  // An empty editor produces "<p></p>" or ""; we normalize both to null on submit.
  const [notes, setNotes] = useState("");
  const [reasonCodeId, setReasonCodeId] = useState<string | null>(null);
  // Free text typed into the autocomplete but never confirmed via the
  // "Add as new reason" row. Held here and resolved to a real ReasonCode on
  // submit, so abandoned typing never mints a code in the shared list.
  const [reasonLabel, setReasonLabel] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState<ConfirmationState | null>(
    null
  );

  // Fix #2: files are staged client-side until Approve/Reject is clicked.
  // stagedFiles: File objects not yet uploaded.
  // uploadingOutcome: true during the upload+log-outcome phase.
  // uploadedStagedFiles: Set of File objects that have already been uploaded
  //   (used to skip re-upload on retry after partial success).
  const [stagedFiles, setStagedFiles] = useState<File[]>([]);
  const [uploadingOutcome, setUploadingOutcome] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const uploadedStagedFilesRef = useRef<Set<File>>(new Set());
  // Bumped whenever a staged file is removed so the hidden <input> is remounted
  // with a fresh, pristine element — guaranteeing the native file picker reopens
  // after a stage→remove cycle.
  const [inputNonce, setInputNonce] = useState(0);
  const fileInputId = `rdd-upload-${submissionCondition?.id ?? "none"}-${inputNonce}`;

  const [, renderTrigger] = useState(moment());
  const [datasource] = useState(
    new Datasource({
      mainModelName: "task",
      renderTrigger: renderTrigger,
    })
  );
  // Separate datasource: its POST must NOT reach the log_outcome subscription
  // below, which treats any successful POST as "outcome logged" and renders the
  // confirmation panel.
  const [reasonDatasource] = useState(
    new Datasource({
      mainModelName: "reason_code",
      renderTrigger: renderTrigger,
    })
  );

  useEffect(() => {
    const sub = datasource.responseSubject$.subscribe(
      (response: DatasourceResponse) => {
        if (
          response.actionType === DatasourceActionType.POST &&
          response.success
        ) {
          setIsSubmitting(false);
          // On log_outcome success: clear staged files and uploading state.
          setStagedFiles([]);
          uploadedStagedFilesRef.current = new Set();
          setUploadingOutcome(false);
          setConfirmation({
            outcome,
            reviewedAt: moment().toISOString(),
            nextRoundNumber:
              outcome === "resubmit_required" && currentRound
                ? currentRound.round_number + 1
                : undefined,
          });
          onSuccess(response.normalizedMainModelResponse);
        } else if (
          response.actionType === DatasourceActionType.POST &&
          !response.success
        ) {
          setIsSubmitting(false);
          // On log_outcome failure: clear uploadingOutcome but keep staged files.
          // Already-uploaded files remain tracked in uploadedStagedFilesRef so
          // a retry does not re-upload them (api-contract §3.2).
          setUploadingOutcome(false);
        }
      }
    );
    return () => sub.unsubscribe();
  }, [datasource, outcome, currentRound, onSuccess]);

  const handleSubmit = useCallback(async () => {
    if (!currentRound) return;
    // Normalize empty rich editor ("<p></p>" / "") to null so the backend
    // stores NULL rather than an empty paragraph string.
    const cleanedNotes = isEmptyNotes(notes) ? null : notes;
    setIsSubmitting(true);

    // Resolve the reason FIRST. When the reviewer typed a reason but never
    // clicked "Add as new reason", we create it here rather than at keystroke
    // time — nothing is written to the shared list unless the outcome is
    // actually logged. POST /reason_codes is idempotent on lower(label)
    // (ReasonCode.find_or_create_from_text), so a label that already exists
    // comes back as the existing record rather than a duplicate.
    let resolvedReasonCodeId = reasonCodeId;
    if (outcome === "resubmit_required" && !resolvedReasonCodeId) {
      const pending = reasonLabel.trim();
      if (!pending) {
        setIsSubmitting(false);
        return;
      }
      const response = await reasonDatasource.post(
        "v2",
        "reason_codes",
        { body: { reason_code: { label: pending } } },
        { updateIsLoding: false }
      );
      // `post` resolves to undefined when the request failed; the datasource has
      // already surfaced the error notification. Abort rather than logging the
      // outcome with a null reason, which the server would reject anyway.
      const created =
        response && (response as any).normalizedMainModelResponse;
      resolvedReasonCodeId = (created && created.id) || null;
      if (!resolvedReasonCodeId) {
        setIsSubmitting(false);
        return;
      }
      // Keep the resolved id so a retry after a log_outcome failure does not
      // re-POST the reason code.
      setReasonCodeId(resolvedReasonCodeId);
    }

    datasource.post(
      "v2",
      `tasks/${taskId}/submission_rounds/${currentRound.id}/log_outcome`,
      {
        body: {
          submission_round: {
            outcome,
            reason_code_id:
              outcome === "resubmit_required" ? resolvedReasonCodeId : null,
            // `notes` carries the serialized HTML including mention spans.
            // See api-contract.md §3 for the exact span format.
            notes: cleanedNotes,
          },
        },
      },
      { updateIsLoding: false }
    );
  }, [
    datasource,
    reasonDatasource,
    taskId,
    currentRound,
    outcome,
    reasonCodeId,
    reasonLabel,
    notes,
  ]);

  /**
   * Stage selected files client-side. No network request is made here.
   * Files are uploaded only when Approve/Reject is clicked (Fix #2).
   */
  const handleFileChange = useCallback(() => {
    if (!uploadRef.current) return;
    const { files } = uploadRef.current;
    if (!files || files.length === 0) return;
    // Snapshot the selected files into a plain array NOW, before clearing the
    // input below. `files` is a *live* FileList bound to input.files; setting
    // `uploadRef.current.value = ""` empties it. Because setStagedFiles' updater
    // runs later (during render), reading the live FileList inside it would see
    // an already-cleared (empty) list — dropping every selected file. Capturing
    // a static array here decouples us from that mutation. See output-47.
    const selectedFiles = Array.from(files) as File[];
    setUploadError(null);
    setStagedFiles((prev) => [...prev, ...selectedFiles]);
    if (uploadRef.current) uploadRef.current.value = "";
  }, []);

  /** Remove a staged (not yet uploaded) file by index. */
  const removeStagedFile = useCallback((index: number) => {
    setStagedFiles((prev) => prev.filter((_, i) => i !== index));
    // Clear the native input AND remount it (via inputNonce → key change) so the
    // picker reliably reopens and selecting the *same* file again still fires
    // onChange (a file input emits no change event when re-picking an identical
    // value). This is what keeps "Attach files" working after a removal.
    if (uploadRef.current) uploadRef.current.value = "";
    setInputNonce((n) => n + 1);
  }, []);

  /**
   * Upload all staged files (that haven't been uploaded yet) sequentially,
   * then call handleSubmit() to log the outcome.
   *
   * Failure semantics (api-contract §3.2):
   * - Any upload fails → abort, do NOT log outcome, keep staged files, surface error.
   * - All uploads succeed but log_outcome fails → keep uploadedStagedFilesRef so
   *   retry only re-issues log_outcome (not re-upload).
   * - Zero staged files → log outcome directly (no upload step).
   */
  const uploadStagedFilesAndLogOutcome = useCallback(async () => {
    if (!currentRound) return;

    // Determine which files still need uploading (skip already-uploaded on retry).
    const filesToUpload = stagedFiles.filter(
      (f) => !uploadedStagedFilesRef.current.has(f)
    );

    if (filesToUpload.length === 0) {
      // No staged files (or all already uploaded on a prior attempt) — log directly.
      handleSubmit();
      return;
    }

    if (!submissionCondition) {
      // No condition to upload to — log directly.
      handleSubmit();
      return;
    }

    setUploadingOutcome(true);
    setUploadError(null);

    // Sequential upload: simpler partial-failure tracking than Promise.all.
    for (const file of filesToUpload) {
      try {
        await uploadFileTracked(dispatch, submissionCondition, file, [RDD_REVIEW_UPLOAD_TAG]);
        // Track as successfully uploaded so a retry skips it.
        uploadedStagedFilesRef.current.add(file);
        if (onUploaded) onUploaded();
      } catch {
        // Upload failed — abort, surface error, keep staged files for retry.
        setUploadError(`Upload failed for "${file.name}". Please try again.`);
        setUploadingOutcome(false);
        return;
      }
    }

    // All uploads succeeded — now log the outcome.
    // uploadingOutcome stays true; it is cleared in the responseSubject$ subscription
    // on both success (cleared + confirmed) and failure (cleared, staged kept).
    handleSubmit();
  }, [currentRound, stagedFiles, submissionCondition, dispatch, onUploaded, handleSubmit]);

  // Not visible when canLogOutcome is false or round is not awaiting review
  if (!canLogOutcome) return null;
  if (!currentRound || currentRound.state !== "awaiting_rdd_review") return null;

  if (confirmation) {
    const isApproved = confirmation.outcome === "approved";
    return (
      <div className="bg-success-light border border-success rounded p-4 mt-4">
        <div className="font-semibold text-success-darker mb-1">
          {isApproved ? "Submission approved" : "Resubmission requested"}
        </div>
        <div className="text-14 text-black-60">
          Logged at {moment(confirmation.reviewedAt).format("D MMM YYYY, h:mm a")}
        </div>
        {!isApproved && confirmation.nextRoundNumber && (
          <div className="text-14 text-black-60 mt-1">
            Round {confirmation.nextRoundNumber} begins when the tenant uploads.
          </div>
        )}
      </div>
    );
  }

  // A confirmed reason OR enough typed text to become one. The length rule is
  // the autocomplete's own MIN_REASON_LENGTH, so the button enables at exactly
  // the moment the "Add as new reason" row appears — the row stays clickable,
  // it is just no longer the only way through.
  const hasPendingReason = reasonLabel.trim().length >= MIN_REASON_LENGTH;
  const resubmitDisabled =
    outcome === "resubmit_required" && !reasonCodeId && !hasPendingReason;

  // Character counter: count the HTML string length because the server validates
  // the raw column value (max 1000 chars of stored HTML). Counting HTML rather
  // than visible text keeps the UI guard in sync with the server-side validation —
  // if we counted only visible text, mention spans would pass the UI check but
  // still exceed the server limit and return a 422.
  const displayLength = isEmptyNotes(notes) ? 0 : notes.length;
  const notesTooLong = displayLength > 1000;

  return (
    <div className="border border-black-20 rounded p-4 mt-4 bg-white">
      <div className="font-semibold text-16 text-black-80 mb-3">
        Log review outcome — Round {currentRound.round_number}
      </div>

      {/* Outcome radios */}
      <div className="flex gap-4 mb-3">
        <label className="flex items-center gap-2 cursor-pointer text-14">
          <input
            type="radio"
            name="outcome"
            value="approved"
            checked={outcome === "approved"}
            onChange={() => {
              setOutcome("approved");
              setReasonCodeId(null);
              setReasonLabel("");
            }}
          />
          <span>Approve</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer text-14">
          <input
            type="radio"
            name="outcome"
            value="resubmit_required"
            checked={outcome === "resubmit_required"}
            onChange={() => setOutcome("resubmit_required")}
          />
          <span>Request resubmission</span>
        </label>
      </div>

      {/* Reason code — only for resubmission */}
      {outcome === "resubmit_required" && (
        <div className="mb-3">
          <label className="block text-13 font-semibold text-black-60 mb-1">
            Resubmission reason
          </label>
          <ReasonCodeAutocomplete
            value={reasonCodeId || undefined}
            onChange={(id, label) => {
              setReasonCodeId(id);
              setReasonLabel(label || "");
            }}
          />
        </div>
      )}

      {/* Notes — rich-text editor with @-mention support */}
      <div className="mb-4">
        <label className="block text-13 font-semibold text-black-60 mb-1">
          Additional notes (optional)
        </label>
        {/*
          * TextInput is React.lazy (DraftText.jsx). Wrap in Suspense so this panel
          * never blocks its parent while the draft-js bundle loads.
          */}
        <Suspense fallback={<div className="text-12 text-black-40">Loading editor…</div>}>
          <TextInput
            users={taggableUsers}
            body={notes}
            onChange={(newBody: string) => setNotes(newBody)}
            placeholder="Additional notes (optional)…"
            hint="Use an @ tag followed by a person's name to mention them. Use 'All' to tag everyone on this task."
            spellCheck={true}
          />
        </Suspense>
        {/* Counter measures HTML length to match the server-side 1000-char limit. */}
        <div className={classNames("text-12 text-right", notesTooLong ? "text-error" : "text-black-40")}>
          {displayLength}/1000
        </div>
        {notesTooLong && (
          <div className="text-12 text-error mt-1">
            Notes too long — please shorten.
          </div>
        )}
      </div>

      {/* RDD document upload widget — only shown when a submission condition is available */}
      {submissionCondition && (
        <div className="mb-4">
          <label className="block text-13 font-semibold text-black-60 mb-1">
            Attach RDD documents (optional)
          </label>
          <div className="flex items-center gap-2 flex-wrap">
            {/*
              * Native <label htmlFor> trigger: clicking the label activates the
              * associated file input via the browser's own label behaviour. This is
              * the most compatible way to open the picker (programmatic
              * input.click() can be blocked in some hardened browser setups).
              * The htmlFor/id pair is rebuilt from a key that changes whenever the
              * staged-file set changes, which forces React to remount the input so
              * the picker reliably reopens after a file is staged and removed.
              */}
            <label
              htmlFor={fileInputId}
              className={classNames(
                "inline-flex items-center gap-1 px-3 py-1 rounded text-13 border",
                uploadingOutcome
                  ? "bg-black-10 text-black-40 border-black-20 cursor-not-allowed"
                  : "bg-white text-primary border-primary hover:bg-black-5 cursor-pointer"
              )}
            >
              <i className="fas fa-paperclip text-12" />
              <span>{uploadingOutcome ? "Uploading…" : "Attach files"}</span>
            </label>
            <input
              key={fileInputId}
              id={fileInputId}
              type="file"
              multiple
              ref={uploadRef}
              className="sr-only"
              disabled={uploadingOutcome}
              onChange={handleFileChange}
              data-testid="rdd-file-input"
            />
            {uploadingOutcome && (
              <span className="text-12 text-black-40" data-testid="upload-spinner">
                <i className="fas fa-spinner fa-spin mr-1" />
                Uploading documents and recording outcome…
              </span>
            )}
          </div>
          {/* Staged file chips — shown for files not yet uploaded */}
          {stagedFiles.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2" data-testid="staged-files-list">
              {stagedFiles.map((file, idx) => (
                <span
                  key={`${file.name}-${file.size}-${file.lastModified}`}
                  className="inline-flex items-center gap-1 px-2 py-0 text-12 text-black-80 bg-black-5 border border-black-20 rounded"
                  data-testid="staged-file-chip"
                >
                  <i className="fas fa-file text-10" />
                  {file.name}
                  <button
                    type="button"
                    aria-label={`Remove ${file.name}`}
                    disabled={uploadingOutcome}
                    onClick={() => removeStagedFile(idx)}
                    className="ml-1 text-black-40 hover:text-error disabled:cursor-not-allowed"
                  >
                    &times;
                  </button>
                </span>
              ))}
            </div>
          )}
          {uploadError && (
            <div className="text-12 text-error mt-1" data-testid="upload-error">
              {uploadError}
            </div>
          )}
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center justify-between">
        <div className="flex gap-2">
          {outcome === "approved" && (
            <button
              className="px-4 py-2 rounded text-14 font-semibold bg-primary text-white hover:bg-primary-dark"
              disabled={isSubmitting || uploadingOutcome || notesTooLong}
              onClick={uploadStagedFilesAndLogOutcome}
            >
              {isSubmitting || uploadingOutcome ? "Uploading…" : "Approve submission"}
            </button>
          )}
          {outcome === "resubmit_required" && (
            <button
              className={classNames(
                "px-4 py-2 rounded text-14 font-semibold",
                resubmitDisabled || notesTooLong || uploadingOutcome
                  ? "bg-black-10 text-black-40 cursor-not-allowed"
                  : "bg-error text-white hover:bg-error-dark"
              )}
              disabled={isSubmitting || resubmitDisabled || notesTooLong || uploadingOutcome}
              onClick={resubmitDisabled || notesTooLong || uploadingOutcome ? undefined : uploadStagedFilesAndLogOutcome}
            >
              {isSubmitting || uploadingOutcome ? "Uploading…" : "Request resubmission"}
            </button>
          )}
        </div>
        <span className="text-12 text-black-40">
          Outcome syncs to Yardi on save.
        </span>
      </div>
    </div>
  );
};

export default memo(ReviewActionPanel);
